<template>
  <div>
    <div v-show="currentStep === 'search'">
      <v-form @submit.prevent="validate()" ref="form">
        <v-btn-toggle mandatory dark background-color="primary" v-model="vehicleLocal">
          <v-btn text small value="driving">Driving</v-btn>
          <v-btn text small value="cycling">Biking</v-btn>
          <v-btn text small value="walking">Walking</v-btn>
        </v-btn-toggle>

        <v-btn text small @click="swapInputs()" color="secondary">
          <v-icon small>fa-exchange-alt</v-icon>
        </v-btn>

        <v-form ref="form" @submit.prevent="validate()">
          <v-container>
            <template v-for="(stop, index) in stops">
              <v-row :key="`stop-${index}`">
                <v-col v-if="stops.length > 2" cols="2" class="px-0">
                  <v-btn
                    small fab color="primary"
                    class="elevation-0"
                    @click="removeStop(index)">
                    <v-icon>fa-minus</v-icon>
                  </v-btn>
                </v-col>
                <v-col class="pa-0">
                  <DirectionsSearch
                    :label="getStopLabel(index)"
                    :stop.sync="stopStore[index]" />
                </v-col>
              </v-row>
              <v-row :key="`addstop-${index}`">
                <v-col class="px-0">
                  <template v-if="index !== stops.length - 1">
                    <v-btn
                      small fab color="primary"
                      class="elevation-0"
                      @click="addBlankStop(index)">
                      <v-icon>fa-plus</v-icon>
                    </v-btn>
                    <span class="primary--text pl-2">Add stop</span>
                  </template>
                </v-col>
              </v-row>
            </template>
          </v-container>

          <v-btn block @click="validate()" color="secondary" :disabled="!stopsAreCompleted">Search</v-btn>
        </v-form>
      </v-form>

      <v-snackbar
        v-model="snackbar.model"
        :bottom="snackbar.bottom"
        :timeout="snackbar.timeout"
        :color="snackbar.color">
        {{ snackbar.message }}
        <v-btn dark text @click="snackbar.model = false">Close</v-btn>
      </v-snackbar>
    </div>
    <div v-show="currentStep === 'results'">
      <div ref="results" />
      <v-btn color="primary" @click="returnToSearch()">
        <v-icon left>fa-arrow-left</v-icon>
        Prev
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import DirectionsSearch from '@/components/home/DirectionsSearch'

export default {
  name: 'Directions',
  components: {
    DirectionsSearch
  },
  data () {
    return {
      currentStep: 'search',
      snackbar: {
        model: false,
        message: '',
        timeout: 6000,
        bottom: true,
        color: 'error'
      }
    }
  },
  watch: {
    async $route (to, from) {
      if (to.query.mode || to.query.vehicle) {
        this.setVehicle(to.query.mode || to.query.vehicle)
      }
    },
    itinerary () {
      this.$refs.results.innerHTML = ''
      this.$refs.results.appendChild(this.itinerary)
    },
    triggerValidation () {
      if (this.triggerValidation) {
        this.validate()
      }
    },
    stops: {
      handler: 'onStopsChange'
    },
    selectedLocation () {
      if (this.selectedLocation) {
        this.setStops([{
          id: this.selectedLocation._id,
          name: this.selectedLocation.name,
          lat: this.selectedLocation.lat,
          lng: this.selectedLocation.lng
        }])
      }
    },
    activeLocations () {
      if (!this.selectedLocation && this.activeLocations.length) {
        const stops = this.activeLocations.map(location => {
          return {
            id: location._id,
            name: location.name,
            lat: location.lat,
            lng: location.lng
          }
        })

        this.setStops(stops)
      }
    },
    vehicle () {
      if (this.$route.query.vehicle !== this.vehicle) {
        this.$router.push({
          params: this.$route.params,
          query: {
            vehicle: this.vehicle
          }
        })
      }
    }
  },
  computed: {
    ...mapGetters([
      'selectedLocation',
      'activeLocations',
      'routingControl',
      'itinerary',
      'vehicle',
      'triggerValidation'
    ]),
    ...mapGetters({
      stopStore: 'stops'
    }),
    stops () {
      let output = this.stopStore

      const format = {
        name: '',
        lat: 0,
        lng: 0
      }

      if (output.length === 0) {
        output.push(format, { ...format })
      } else if (output.length === 1) {
        output.push(format)
      }

      // Verify that the output has all of the required properties
      output = output.map(stop => {
        const stopOutput = stop
        Object.entries(format).forEach(entry => {
          const [key, value] = entry

          if (stopOutput && !(key in stopOutput)) {
            stopOutput[key] = value
          }
        })
        return stopOutput
      })

      return output
    },
    stopsAreCompleted () {
      if (!this.stops) return false

      let output = true

      this.stops.every(stop => {
        if (stop && stop.name === '') {
          output = false
          return false
        } else {
          return true
        }
      })

      return output
    },
    vehicleLocal: {
      get () {
        return this.vehicle
      },
      set (input) {
        this.setVehicle(input)
      }
    }
  },
  mounted () {
    if (this.selectedLocation) {
      this.setStops([{
        id: this.selectedLocation._id,
        name: this.selectedLocation.name,
        lat: this.selectedLocation.lat,
        lng: this.selectedLocation.lng
      }])
    } else if (this.activeLocations.length > 0) {
      const stops = this.activeLocations.map(location => {
        return {
          id: location._id,
          name: location.name,
          lat: location.lat,
          lng: location.lng
        }
      })

      this.setStops(stops)
    }

    const runStopsChange = this.stops.some(stop => stop.name !== '' || stop.lat !== 0 || stop.lng !== 0)
    if (runStopsChange) this.onStopsChange()

    if (this.$route.query.mode || this.$route.query.vehicle) {
      this.setVehicle(this.$route.query.mode || this.$route.query.vehicle)
    }

    if (this.triggerValidation) {
      this.validate()
    }
  },
  beforeDestroy () {
    this.reset()
  },
  methods: {
    ...mapMutations([
      'setVehicle',
      'setItinerary',
      'setStops'
    ]),
    ...mapActions([
      'addStop',
      'removeStop'
    ]),
    swapInputs () {
      this.setStops(this.stops.reverse())
    },
    async validate () {
      if (this.$refs.form.validate()) {
        const waypoints = this.stops.map(stop => {
          return [stop.lat, stop.lng]
        })

        const itinerary = await this.getDirections(waypoints)
        this.setItinerary(itinerary)

        this.currentStep = 'results'
      }
    },
    getStopLabel (index) {
      switch (index) {
        case 0:
          return 'From...'
        case (this.stops.length - 1):
          return 'To...'
        default:
          return `Step ${index + 1}`
      }
    },
    addBlankStop (index) {
      const stop = {
        id: '',
        name: '',
        lat: 0,
        lng: 0
      }
      this.addStop({ index, stop })
    },
    returnToSearch () {
      this.routingControl.spliceWaypoints(0, this.routingControl.getWaypoints().length)

      this.currentStep = 'search'
    },
    reset () {
      this.returnToSearch()
      this.setStops([])
    },
    async getDirections (waypoints) {
      this.routingControl.setWaypoints(waypoints)

      await this.$nextTick()

      const itinerary = this.routingControl.onAdd(this.routingControl._map)
      return itinerary
    },
    onStopsChange () {
      this.$router.push({
        params: {
          id: this.stops
            .filter(stop => stop.id || (stop.lat && stop.lng))
            .map(stop => {
              if (stop.id) return stop.id
              else if (stop.lat && stop.lng) return `${stop.lat};${stop.lng}`
            })
            .join(',')
        },
        query: this.$route.query
      })
    }
  }
}
</script>

<style scoped lang="scss">
// Including #app to fix a CSS load order bug
#app {
  .v-item-group {
    display: inline-flex;

    .v-btn {
      border-radius: 0;
      justify-content: center;
      margin: 0;
      min-width: auto;
      padding: 0 8px;
      width: auto;

      &:not(:last-child) {
        border-right: 1px solid transparent;
        border-color: rgba(0,0,0,.12) !important;
      }

      &:first-child {
        border-radius: 4px 0 0 4px;
      }

      &:last-child {
        border-radius: 0 4px 4px 0;
      }
    }
  }
}
</style>
